import React, { useEffect, useMemo, useState } from 'react';
import { apiBaseUrl, buildApplyUrl } from '../data';
import { parseApiResponse } from '../utils/api';

const fallbackJobs = [
  {
    title: 'Fresher Openings',
    slug: 'fresher-openings',
    summary: 'Entry-level roles for graduates who need placement support, resume guidance, and interview coaching.',
    department: 'Launch Track',
    location: 'India',
    experience: '0-1 years',
    employmentType: 'Career Support',
    responsibilities: 'Resume preparation, interview scheduling, and entry-level role matching.',
    requirements: 'Recent graduates with basic communication skills and readiness for interviews.',
  },
  {
    title: 'Lateral Positions',
    slug: 'lateral-positions',
    summary: 'Role matching for experienced candidates who want a better company, salary package, or technology stack.',
    department: 'Growth Track',
    location: 'Hybrid',
    experience: '2+ years',
    employmentType: 'Career Support',
    responsibilities: 'Profile positioning, relevant role matching, and salary negotiation support.',
    requirements: 'Prior work experience, updated resume, current CTC details, and preferred location.',
  },
  {
    title: 'Upskilling Tracks',
    slug: 'upskilling-tracks',
    summary: 'Short support programs that prepare candidates for urgent market demand in cloud, support, and delivery roles.',
    department: 'Skill Track',
    location: 'Remote',
    experience: 'Flexible',
    employmentType: 'Training Support',
    responsibilities: 'Skill planning, guided training, and placement-oriented preparation.',
    requirements: 'Interest in learning current market skills and availability for guided sessions.',
  },
];

const normalizeJob = (job, index = 0) => ({
  id: job.id || job.slug || job.title,
  title: job.title,
  slug: job.slug || job.title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, ''),
  summary: job.summary || job.text || '',
  department: job.department || 'General Hiring',
  location: job.location || 'India',
  experience: job.experience || 'Experience not specified',
  employmentType: job.employment_type || job.employmentType || 'Full Time',
  responsibilities: job.responsibilities || '',
  requirements: job.requirements || '',
  accent: ['blue', 'coral', 'mint'][index % 3],
});

const splitDetailText = (text) => (
  String(text || '')
    .split(/\r?\n|[•]/)
    .map((item) => item.trim())
    .filter(Boolean)
);

export default function JobsPage({ selectedJob = '' }) {
  const [jobs, setJobs] = useState(fallbackJobs.map(normalizeJob));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;

    const loadJobs = async () => {
      try {
        const response = await fetch(`${apiBaseUrl}/jobs/`);
        const result = await parseApiResponse(response);

        if (!response.ok) {
          throw new Error('Could not load jobs.');
        }

        if (active && Array.isArray(result.results) && result.results.length) {
          setJobs(result.results.map(normalizeJob));
        }
      } catch (_error) {
        if (active) {
          setJobs(fallbackJobs.map(normalizeJob));
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadJobs();
    return () => {
      active = false;
    };
  }, []);

  const selectedJobRecord = useMemo(() => {
    if (!selectedJob) {
      return null;
    }

    return jobs.find((job) => job.slug === selectedJob || job.title === selectedJob) || null;
  }, [jobs, selectedJob]);

  if (selectedJob && selectedJobRecord) {
    const responsibilities = splitDetailText(selectedJobRecord.responsibilities);
    const requirements = splitDetailText(selectedJobRecord.requirements);

    return (
      <section className="page-section jobs-page-section jobs-simple-section">
        <div className="jobs-detail-shell">
          <a href="/jobs" className="jobs-back-link">Back to openings</a>

          <div className="jobs-detail-page">
            <header className="jobs-detail-header">
              <span className="jobs-detail-kicker">{selectedJobRecord.department}</span>
              <h1>{selectedJobRecord.title}</h1>
              <p>{selectedJobRecord.summary}</p>
              <a href={buildApplyUrl(selectedJobRecord.title)} className="jobs-apply-button">Apply Now</a>
            </header>

            <div className="jobs-detail-meta">
              <div><span>Location</span><strong>{selectedJobRecord.location}</strong></div>
              <div><span>Experience</span><strong>{selectedJobRecord.experience}</strong></div>
              <div><span>Type</span><strong>{selectedJobRecord.employmentType}</strong></div>
            </div>

            <section className="jobs-detail-block">
              <h2>Responsibilities</h2>
              {responsibilities.length ? (
                <ul>
                  {responsibilities.map((item) => <li key={item}>{item}</li>)}
                </ul>
              ) : (
                <p>{selectedJobRecord.summary}</p>
              )}
            </section>

            <section className="jobs-detail-block">
              <h2>Requirements</h2>
              {requirements.length ? (
                <ul>
                  {requirements.map((item) => <li key={item}>{item}</li>)}
                </ul>
              ) : (
                <p>Our team will map the exact requirement after reviewing your profile.</p>
              )}
            </section>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="page-section jobs-page-section jobs-simple-section">
      <div className="jobs-simple-header">
        <h1>Job Openings</h1>
        <p>{loading ? 'Loading openings...' : 'Choose an opening and view the full requirement before applying.'}</p>
      </div>

      <div className="jobs-card-grid">
        {jobs.map((job) => (
          <article key={job.id} className={`jobs-simple-card jobs-simple-card-${job.accent}`}>
            <div className="jobs-simple-card-head">
              <span>{job.department}</span>
              <strong>{job.experience}</strong>
            </div>
            <h2>{job.title}</h2>
            <p>{job.summary}</p>
            <div className="jobs-simple-card-meta">
              <span>{job.location}</span>
              <span>{job.employmentType}</span>
            </div>
            <a href={`/jobs?job=${encodeURIComponent(job.slug)}`} className="jobs-view-button">View Details</a>
          </article>
        ))}
      </div>
    </section>
  );
}
